/**
 * Canonical Bundesland authority (single source of truth).
 *
 * This module unifies the three previously-divergent representations of the
 * 16 German Bundesländer that lived independently across the monorepo:
 *
 *   1. Marketplace/display — the full umlaut name ("Nordrhein-Westfalen").
 *      Was `BUNDESLAENDER` + `BUNDESLAND_TO_ISO_CODE` in
 *      apps/website/src/lib/admin/constants.ts, plus `bundeslandToCode` in
 *      apps/website/src/lib/naturraum/aliases.ts.
 *   2. Cadastral/geo slug — the lowercase-ASCII-hyphen key
 *      ("nordrhein-westfalen"). Was `normalizeBundeslandName` +
 *      `BUNDESLAND_ALIASES` + `getBundeslandKey` in @oekopunktemarkt/cadastral
 *      (and a hand-copy in apps/landvest/src/lib/cadastral/utils.ts).
 *   3. Import fuzzy-matching — `normalizeBundesland` in
 *      apps/website/src/lib/projects/import/mapper.ts.
 *
 * The DB table `public.metadata_bundeslaender (code, name)` is the data
 * source of truth; this constant mirrors it and is locked to it by
 * `seed-parity.test.ts`. Storage everywhere is the canonical umlaut NAME;
 * the slug and ISO code are DERIVED here, never stored as a rival source.
 */

/** The 16 canonical Bundesland names (full German, with umlauts). Mirrors
 *  `metadata_bundeslaender.name` — locked by seed-parity.test.ts. */
export const BUNDESLAENDER = [
  "Baden-Württemberg",
  "Bayern",
  "Berlin",
  "Brandenburg",
  "Bremen",
  "Hamburg",
  "Hessen",
  "Mecklenburg-Vorpommern",
  "Niedersachsen",
  "Nordrhein-Westfalen",
  "Rheinland-Pfalz",
  "Saarland",
  "Sachsen",
  "Sachsen-Anhalt",
  "Schleswig-Holstein",
  "Thüringen",
] as const;

export type Bundesland = (typeof BUNDESLAENDER)[number];

/** ISO 3166-2:DE code per Bundesland. Mirrors `metadata_bundeslaender.code`. */
export const BUNDESLAND_TO_ISO_CODE: Record<Bundesland, string> = {
  "Baden-Württemberg": "BW",
  Bayern: "BY",
  Berlin: "BE",
  Brandenburg: "BB",
  Bremen: "HB",
  Hamburg: "HH",
  Hessen: "HE",
  "Mecklenburg-Vorpommern": "MV",
  Niedersachsen: "NI",
  "Nordrhein-Westfalen": "NW",
  "Rheinland-Pfalz": "RP",
  Saarland: "SL",
  Sachsen: "SN",
  "Sachsen-Anhalt": "ST",
  "Schleswig-Holstein": "SH",
  Thüringen: "TH",
};

export const VALID_ISO_CODES: ReadonlySet<string> = new Set(Object.values(BUNDESLAND_TO_ISO_CODE));

const ISO_TO_BUNDESLAND: Record<string, Bundesland> = Object.fromEntries(
  (Object.entries(BUNDESLAND_TO_ISO_CODE) as [Bundesland, string][]).map(([name, code]) => [
    code,
    name,
  ])
) as Record<string, Bundesland>;

/**
 * Resolve a 2-letter ISO 3166-2:DE code (case-sensitive uppercase) to the
 * canonical name. Returns null for unknown codes.
 */
export function fromIsoCode(code: string | null | undefined): Bundesland | null {
  if (!code) return null;
  return ISO_TO_BUNDESLAND[code] ?? null;
}

/**
 * Resolve a Bundesland string (full name like "Nordrhein-Westfalen" or an
 * ISO code like "NW") to the canonical ISO 3166-2 code. Returns null if
 * unrecognised.
 *
 * Behaviour is IDENTICAL to the former `bundeslandToCode`
 * (apps/website/src/lib/naturraum/aliases.ts) — pinned by aliases.test.ts:
 * case-sensitive full-name match OR a valid 2-letter uppercase code.
 */
export function toIsoCode(input: string | null | undefined): string | null {
  if (!input) return null;
  const trimmed = input.trim();
  if (trimmed.length === 2 && /^[A-Z]{2}$/.test(trimmed)) {
    return VALID_ISO_CODES.has(trimmed) ? trimmed : null;
  }
  return BUNDESLAND_TO_ISO_CODE[trimmed as Bundesland] ?? null;
}

/**
 * Normalize a Bundesland name to its cadastral config key form
 * (lowercase, umlauts transliterated, spaces → hyphens).
 *
 * IDENTICAL to the former `normalizeBundeslandName` in
 * @oekopunktemarkt/cadastral — pinned by packages/cadastral/__tests__/utils.test.ts.
 * e.g. "Baden-Württemberg" → "baden-wuerttemberg", "Bayern" → "bayern".
 */
export function normalizeBundeslandName(name: string): string {
  return name
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/\s+/g, "-");
}

/**
 * Map common Bundesland name variations to the cadastral slug key.
 *
 * Copied VERBATIM from @oekopunktemarkt/cadastral `BUNDESLAND_ALIASES`
 * (so `toSlug`/`getBundeslandKey` stay behaviour-identical), with ONE
 * intentional addition: `nds` → Niedersachsen (a real-world abbreviation
 * seen in prod `epf_analyses.bundesland`; previously returned null).
 */
const BUNDESLAND_SLUG_ALIASES: Record<string, string> = {
  // Standard names (already normalized)
  "nordrhein-westfalen": "nordrhein-westfalen",
  bayern: "bayern",
  "baden-wuerttemberg": "baden-wuerttemberg",
  berlin: "berlin",
  brandenburg: "brandenburg",
  bremen: "bremen",
  hamburg: "hamburg",
  hessen: "hessen",
  "mecklenburg-vorpommern": "mecklenburg-vorpommern",
  niedersachsen: "niedersachsen",
  "rheinland-pfalz": "rheinland-pfalz",
  saarland: "saarland",
  sachsen: "sachsen",
  "sachsen-anhalt": "sachsen-anhalt",
  "schleswig-holstein": "schleswig-holstein",
  thueringen: "thueringen",

  // Common variations
  nrw: "nordrhein-westfalen",
  "nordrhein westfalen": "nordrhein-westfalen",
  by: "bayern",
  bw: "baden-wuerttemberg",
  "baden-württemberg": "baden-wuerttemberg",
  "baden wuerttemberg": "baden-wuerttemberg",
  "baden württemberg": "baden-wuerttemberg",
  be: "berlin",
  bb: "brandenburg",
  hb: "bremen",
  hh: "hamburg",
  he: "hessen",
  mv: "mecklenburg-vorpommern",
  "mecklenburg vorpommern": "mecklenburg-vorpommern",
  ni: "niedersachsen",
  nds: "niedersachsen", // intentional addition (prod epf_analyses variant)
  rp: "rheinland-pfalz",
  // Intentional additions: umlaut-DROPPED Title-case variants seen in prod
  // (requests/offerings `Baden-Wurttemberg`, `Thuringen` — ü→u, not ü→ue).
  // Württemberg and Thüringen are the only two of the 16 names with umlauts.
  "baden-wurttemberg": "baden-wuerttemberg",
  thuringen: "thueringen",
  "rheinland pfalz": "rheinland-pfalz",
  sl: "saarland",
  sn: "sachsen",
  st: "sachsen-anhalt",
  "sachsen anhalt": "sachsen-anhalt",
  sh: "schleswig-holstein",
  "schleswig holstein": "schleswig-holstein",
  th: "thueringen",
  thüringen: "thueringen",
};

/**
 * Get the cadastral slug key from any name variation. Returns null if
 * unrecognised. IDENTICAL to the former `getBundeslandKey`
 * (@oekopunktemarkt/cadastral) modulo the `nds` addition above.
 */
export function getBundeslandKey(name: string | null | undefined): string | null {
  if (!name) return null;
  const normalized = normalizeBundeslandName(name);
  return BUNDESLAND_SLUG_ALIASES[normalized] || null;
}

/** Alias for {@link getBundeslandKey} reading better at call sites that want
 *  "the slug for this canonical name". */
export const toSlug = getBundeslandKey;

/** fold(canonicalName) → canonicalName, e.g. "nordrhein-westfalen" →
 *  "Nordrhein-Westfalen". Derived from BUNDESLAENDER so it can never drift. */
const SLUG_TO_CANONICAL: Record<string, Bundesland> = Object.fromEntries(
  BUNDESLAENDER.map((name) => [normalizeBundeslandName(name), name])
) as Record<string, Bundesland>;

/**
 * Steps 1–3 of {@link toCanonicalName} for a trimmed, non-empty input: the
 * canonical name, an ISO code, or a fold / alias-table spelling of ONE state.
 */
function knownSpelling(trimmed: string): Bundesland | null {
  // 1. Exact canonical name (fast path)
  if ((BUNDESLAENDER as readonly string[]).includes(trimmed)) {
    return trimmed as Bundesland;
  }

  // 2. ISO 3166-2 code (2-letter, case-insensitive)
  const upper = trimmed.toUpperCase();
  if (upper.length === 2 && ISO_TO_BUNDESLAND[upper]) {
    return ISO_TO_BUNDESLAND[upper];
  }

  // 3. Folded match: case / umlaut-stripped / space-vs-hyphen variants + the
  //    alias table (nrw, nds, etc.). The slug is itself a folded canonical key.
  const folded = normalizeBundeslandName(trimmed);
  const slug = BUNDESLAND_SLUG_ALIASES[folded] ?? folded;
  return SLUG_TO_CANONICAL[slug] ?? null;
}

/**
 * Comprehensive normalizer: resolve ANY known Bundesland representation
 * (canonical name, ISO code, cadastral slug, ASCII transliteration, common
 * abbreviation incl. `nds`, space-separated form, mixed case) to the
 * canonical umlaut name. Returns null if unrecognised.
 *
 * Designed as a strict SUPERSET of the former
 * `normalizeBundesland` (apps/website/src/lib/projects/import/mapper.ts):
 * every input that produced a non-null result there produces the same result
 * here; additional inputs (slugs, ISO codes, `nds`) now resolve too. The
 * loose substring fallback preserves the legacy fuzzy-match tail.
 */
export function toCanonicalName(input: string | null | undefined): Bundesland | null {
  if (input == null) return null;
  const trimmed = String(input).trim();
  if (trimmed === "") return null;

  const known = knownSpelling(trimmed);
  if (known) return known;

  // 4. Loose substring fallback (preserves legacy normalizeBundesland tail,
  //    e.g. "Freistaat Bayern" → "Bayern").
  const lower = trimmed.toLowerCase();
  for (const bl of BUNDESLAENDER) {
    const blLower = bl.toLowerCase();
    if (blLower.includes(lower) || lower.includes(blLower)) return bl;
  }

  return null;
}

/**
 * Strict variant of `toCanonicalName` for BOUNDARY input — API query params and
 * cross-app payloads (the ÖPM↔LandVest integration routes, the supply mint).
 *
 * Accepts the canonical name and its exact fold variants only: the ASCII
 * transliteration LandVest's scoring tables use ("Baden-Wuerttemberg"), case
 * and space/hyphen differences. REFUSES everything that only resolves through
 * an alias, an ISO code or the substring fallback — "er" would otherwise become
 * a confident wrong state, and at a filter boundary a wrong state is worse
 * than no match. Round-trip guard: fold(canonical) === fold(input) (the shape
 * the team converged on for the supply mint, review 2026-08-05).
 *
 * Returns null for an unrecognisable value; callers must treat that as a
 * refusal (400 / no rows), never as "no filter" — dropping the filter widens a
 * capability or demand claim to every state.
 */
export function toCanonicalNameStrict(input: string | null | undefined): Bundesland | null {
  const canonical = toCanonicalName(input);
  if (!canonical) return null;
  const folded = normalizeBundeslandName(String(input).trim());
  return normalizeBundeslandName(canonical) === folded ? canonical : null;
}

/**
 * The words that name a state inside free text, folded like the input: every
 * canonical name, and the alias-table spellings of three letters or more
 * ("nrw", "nds", "thuringen"). Two-letter codes are left out — "be", "he" and
 * "st" are ordinary words.
 */
const STATE_WORDS: ReadonlyArray<readonly [string, Bundesland]> = [
  ...BUNDESLAENDER.map((name) => [normalizeBundeslandName(name), name] as const),
  ...Object.entries(BUNDESLAND_SLUG_ALIASES)
    .filter(([key]) => /^[a-z][a-z-]{2,}$/.test(key) && !SLUG_TO_CANONICAL[key])
    .flatMap(([key, slug]) => {
      const state = SLUG_TO_CANONICAL[slug];
      return state ? [[key, state] as const] : [];
    }),
];

/**
 * `toCanonicalName` for FREE TEXT — a Bundesland as a chat model or a user
 * writes it: "NRW", "Thueringen", "Freistaat Bayern", "Land Sachsen-Anhalt".
 *
 * The spellings `toCanonicalName` knows are accepted as they are. Its substring
 * fallback is not: instead the text must name exactly ONE state as a whole
 * word. "Rheinland" (only the start of Rheinland-Pfalz), "West", "Rheinhessen",
 * "Brandenburg (bei Berlin)" and "Sachsen und Sachsen-Anhalt" return null. A
 * state stored on a lead decides what it is matched with, so an unclear value
 * has to be asked about, not guessed. A name inside a longer one ("Sachsen" in
 * "Sachsen-Anhalt") is part of that name, not a second state.
 */
export function toCanonicalNameUnambiguous(input: string | null | undefined): Bundesland | null {
  if (input == null) return null;
  const trimmed = String(input).trim();
  if (trimmed === "") return null;

  const known = knownSpelling(trimmed);
  if (known) return known;

  const folded = normalizeBundeslandName(trimmed);
  const hits: Array<{ state: Bundesland; start: number; end: number }> = [];
  for (const [word, state] of STATE_WORDS) {
    for (const m of folded.matchAll(new RegExp(`(?<![a-z])${word}(?![a-z])`, "g"))) {
      hits.push({ state, start: m.index, end: m.index + word.length });
    }
  }
  const standalone = hits.filter(
    (hit) =>
      !hits.some(
        (other) =>
          other.end - other.start > hit.end - hit.start &&
          other.start <= hit.start &&
          other.end >= hit.end
      )
  );
  const states = new Set(standalone.map((hit) => hit.state));
  return states.size === 1 ? ([...states][0] ?? null) : null;
}

/** True if `value` is exactly one of the 16 canonical names. */
export function isCanonicalBundesland(value: unknown): value is Bundesland {
  return typeof value === "string" && (BUNDESLAENDER as readonly string[]).includes(value);
}
